import { datadogRum } from "@datadog/browser-rum";
import { type Enterprise } from "@mg/schemas/src/commons";
import {
  type LoginResponse,
  type ShowcaseToken,
} from "@mg/schemas/src/prince/auth";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";

import { persistInPlaceAskSurface } from "../../config/askSurface";
import { posthogClient } from "../../config/posthog";
import {
  type DemoPersona,
  getStoredDemoPersona,
  setStoredDemoPersona,
} from "../demoPersona";
import { MessageType } from "../figma-plugin-types";
import { postToParent } from "../parent-messaging";
import { queryClient } from "../queryClient";
import { safeStorageGet, safeStorageRemove, safeStorageSet } from "../storage";

/** Origin-local skip for /add-password while viewing as an impersonated user. */
export const IMPERSONATION_BYPASS_STORAGE_KEY = "impersonation_bypass";
/** Pairs with the bypass flag so a claim-less refresh keeps the same session. */
export const IMPERSONATION_BYPASS_USER_ID_KEY = "impersonation_bypass_user_id";

function syncImpersonationBypassStorage(
  decodedToken: ShowcaseToken | null,
): void {
  if (decodedToken?.impersonatorEmail) {
    safeStorageSet(localStorage, IMPERSONATION_BYPASS_STORAGE_KEY, "true");
    if (decodedToken.userID) {
      safeStorageSet(
        localStorage,
        IMPERSONATION_BYPASS_USER_ID_KEY,
        decodedToken.userID,
      );
    }
    return;
  }
  if (!decodedToken) {
    safeStorageRemove(localStorage, IMPERSONATION_BYPASS_STORAGE_KEY);
    safeStorageRemove(localStorage, IMPERSONATION_BYPASS_USER_ID_KEY);
    return;
  }
  const bypassUserId = safeStorageGet(
    localStorage,
    IMPERSONATION_BYPASS_USER_ID_KEY,
  );
  if (
    bypassUserId &&
    decodedToken.userID &&
    bypassUserId === decodedToken.userID
  ) {
    safeStorageSet(localStorage, IMPERSONATION_BYPASS_STORAGE_KEY, "true");
    return;
  }
  safeStorageRemove(localStorage, IMPERSONATION_BYPASS_STORAGE_KEY);
  safeStorageRemove(localStorage, IMPERSONATION_BYPASS_USER_ID_KEY);
}

/**
 * Inlined mirror of `isDemoEnterprise()` from `../auth`. Importing
 * `utils/auth.ts` here creates a boot-order cycle (this slice is loaded
 * by `store.ts`, which `utils/auth.ts` transitively reaches via
 * `loginRoute`). Reads the same `["enterprises"]` react-query cache, so
 * stays in lockstep with the public helper.
 */
function isOnDemoEnterprise(value: ShowcaseToken | null): boolean {
  if (!value?.enterpriseId) return false;
  const enterprises = queryClient.getQueryData<Enterprise[]>(["enterprises"]);
  if (!enterprises) return false;
  const current = enterprises.find((e) => e._id === value.enterpriseId);
  return current?.isDemo === true;
}

/**
 * Layer the demo persona overlay (if any) on top of a decoded token.
 * The overlay only ever touches `name` and `avatar` — all auth-critical
 * fields (`userID`, `email`, `role`, `enterpriseId`, etc.) are
 * preserved. Returns the input untouched when no persona is active or
 * when the user is not currently on the demo enterprise — without this
 * gate an MG admin who sets a persona on the demo tenant and then
 * switches to a real customer tenant would still see the persona name
 * and avatar overlaid on the prod surface until logout.
 */
function withDemoPersona(value: ShowcaseToken | null): ShowcaseToken | null {
  if (!value) return value;
  if (!isOnDemoEnterprise(value)) return value;
  const persona = getStoredDemoPersona();
  if (!persona) return value;
  return {
    ...value,
    name: persona.name,
    avatar: persona.avatar ?? value.avatar,
  };
}

type AuthState = {
  /*
  The relevant fields are typically:
  - If authenticated: email, enterpriseId, exp, name, role, userID
  - If unauthenticated: enterpriseId, exp, referredBy, ticketId/folderId
  */
  value: ShowcaseToken | null;
  loggingOut: boolean;
};

const initialState: AuthState = {
  value: null,
  loggingOut: false,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     * Given a token, decodes the token and sets the user object.
     * Note: This should be invoked immediately after hitting an endpoint that
     * returns a token. This action does not check for token expiry before
     * setting the user. To check token expiry, use `derive` instead.
     */
    login(state, action: PayloadAction<LoginResponse>) {
      const decoded = jwtDecode<ShowcaseToken>(action.payload.token, {});

      // will error at this point if invalid token.

      safeStorageSet(localStorage, "token", action.payload.token);
      sendTokenToServiceWorker();

      // why: RUM gets the id alone — the decoded JWT carries email, name, permissions and impersonatorEmail, which must not reach RUM or session replay
      datadogRum.setUser({ id: decoded.userID });
      posthogClient.reset();
      if (decoded.userID) {
        posthogClient.identify(decoded.userID, {
          email: decoded.email,
          name: decoded.name,
          role: decoded.role,
          enterpriseId: decoded.enterpriseId,
          enterpriseName: decoded.enterpriseName ?? decoded.company,
        });
        if (decoded.enterpriseId) {
          posthogClient.group("enterprise", decoded.enterpriseId);
        }
      }
      state.value = withDemoPersona(decoded);

      // Apply saved language preference from the user profile. This takes
      // priority over browser detection. Import is dynamic to avoid a
      // circular dependency (i18n config → React → Redux → auth slice).
      if (decoded.uiLanguage) {
        import("../../i18n/config").then(({ applyProfileLanguage }) =>
          applyProfileLanguage(decoded.uiLanguage),
        );
      }

      // Force refetch of all user-related data
      queryClient.refetchQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          const relevantTerms = [
            "user",
            "profile",
            "comment",
            "avatar",
            "name",
            "ticket",
            "participant",
            "reviewer",
            "creative",
            "folder",
            "project",
            "message",
            "mention",
          ];
          return (
            Array.isArray(queryKey) &&
            queryKey.some(
              (key) =>
                typeof key === "string" &&
                relevantTerms.some((term) => key.toLowerCase().includes(term)),
            )
          );
        },
      });
    },
    /**
     * Removes the token from localStorage and sets the state to `null`.
     */
    logout(state, action: PayloadAction<string | undefined>) {
      // Set logging out state to prevent API calls during logout
      state.loggingOut = true;

      safeStorageRemove(localStorage, "token");
      safeStorageRemove(localStorage, "onboarding_flow");
      safeStorageRemove(localStorage, IMPERSONATION_BYPASS_STORAGE_KEY);
      safeStorageRemove(localStorage, IMPERSONATION_BYPASS_USER_ID_KEY);
      persistInPlaceAskSurface(false);
      setStoredDemoPersona(null);
      sendTokenToServiceWorker();
      state.value = null;

      // The session cookie is cleared by the store's logout listener
      // (`utils/store.ts`): the request reads the store, which Redux forbids
      // while this reducer runs.

      // reset query client
      queryClient.invalidateQueries();
      queryClient.clear();

      // reset analytics
      posthogClient.reset();

      // reset Datadog user
      datadogRum.setUser({});

      if (action.payload != null) {
        safeStorageSet(localStorage, "redirect", action.payload);
      }

      // Tell the expected embedder (Figma plugin UI / Chrome extension popup)
      // the session ended; postToParent drops this for any other frame.
      postToParent({
        type: MessageType.LOGOUT,
        sender: "web",
      });

      // Reset logging out state
      state.loggingOut = false;
    },
    derive(state) {
      // TODO: use this temporarily until parsed search is added back to router.
      const search = new URLSearchParams(window.location.search);
      const urlToken = search.get("t");
      const refreshedToken = search.get("refreshedToken");
      const localToken = safeStorageGet(localStorage, "token");
      const listRedirect = search.get("r");
      const tokenIsExpired = (token: ShowcaseToken) =>
        Date.now() / 1000 > (token.exp as number);

      function safeDecodeToken<T>(token: string | null): T | null {
        if (!token) return null;
        try {
          return jwtDecode<T>(token);
        } catch {
          return null;
        }
      }

      let defaultUser: (ShowcaseToken & { listPath?: string }) | null = null;
      const decodedUrlToken = safeDecodeToken<ShowcaseToken>(urlToken);
      if (decodedUrlToken && !tokenIsExpired(decodedUrlToken)) {
        // urlToken is non-null when decodedUrlToken is truthy
        safeStorageSet(localStorage, "urlToken", urlToken!);
        search.delete("t");
      }

      function applyToken(decodedToken: ShowcaseToken, token: string) {
        defaultUser = decodedToken;
        safeStorageSet(localStorage, "token", token);

        // why: RUM gets the id alone — the decoded JWT carries email, name, permissions and impersonatorEmail, which must not reach RUM or session replay
        datadogRum.setUser({ id: decodedToken.userID });
        if (token !== localToken) {
          posthogClient.reset();
          if (decodedToken.userID) {
            posthogClient.identify(decodedToken.userID, {
              email: decodedToken.email,
              name: decodedToken.name,
              role: decodedToken.role,
              enterpriseId: decodedToken.enterpriseId,
              enterpriseName:
                decodedToken.enterpriseName ?? decodedToken.company,
            });
            if (decodedToken.enterpriseId) {
              posthogClient.group("enterprise", decodedToken.enterpriseId);
            }
          }
        }
      }

      // why: an admin who starts a WorkOS impersonation while signed in as
      // themselves would otherwise keep their own session — the local token
      // below outranks the URL token for every other ?t= link.
      const urlTokenIsImpersonation =
        decodedUrlToken != null &&
        !tokenIsExpired(decodedUrlToken) &&
        Boolean(decodedUrlToken.impersonatorEmail);

      let decodedToken: ShowcaseToken | null = null;
      // If there is a valid fresh token, always use that
      if (refreshedToken) {
        const decodedRefreshedToken =
          safeDecodeToken<ShowcaseToken>(refreshedToken);
        if (decodedRefreshedToken) {
          applyToken(decodedRefreshedToken, refreshedToken);
          decodedToken = decodedRefreshedToken;
          // why: refreshedToken is the cross-origin session handoff — leaving it (or a leftover t=) in the query keeps the JWT in history and copied links
          search.delete("refreshedToken");
          search.delete("t");
          const qs = search.toString();
          const next = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
          window.history.replaceState(window.history.state, "", next);
        }
      }
      // If there is a valid local token, prefer it over URL token
      if (!decodedToken && localToken && !urlTokenIsImpersonation) {
        const decodedLocalToken = safeDecodeToken<ShowcaseToken>(localToken);
        if (
          decodedLocalToken &&
          !tokenIsExpired(decodedLocalToken) &&
          (decodedLocalToken.email ||
            !decodedUrlToken ||
            tokenIsExpired(decodedUrlToken))
        ) {
          decodedToken = decodedLocalToken;
          applyToken(decodedLocalToken, localToken);
        }
      }
      // Else if there is a valid URL token, use that
      if (
        !decodedToken &&
        decodedUrlToken &&
        !tokenIsExpired(decodedUrlToken)
      ) {
        decodedToken = decodedUrlToken;
        if (!decodedToken.email) {
          if (decodedToken.ticketId) {
            // urlToken is non-null when decodedUrlToken is truthy
            safeStorageSet(
              sessionStorage,
              `${decodedToken.ticketId}|token`,
              urlToken!,
            );
          } else if (decodedToken.folderId) {
            safeStorageSet(
              sessionStorage,
              `${decodedToken.folderId}|token`,
              urlToken!,
            );
          }
        }

        applyToken(decodedToken, urlToken!);
      }
      // why: the impersonated user may never have finished onboarding; without
      // a bypass flag the auth layout sends the admin to /add-password.
      // Use a dedicated key — `onboarding_flow` is shared with invite signup and
      // must not leak after impersonation ends on the same browser tab. Refreshed
      // tokens drop impersonatorEmail, so pair the flag with userID (see helper).
      syncImpersonationBypassStorage(decodedToken);
      // If none of the above tokens are valid, clear stored token
      if (!decodedToken) {
        safeStorageRemove(localStorage, "token");
      }

      if (listRedirect) {
        safeStorageSet(localStorage, "listPath", listRedirect);
      }

      sendTokenToServiceWorker();
      state.value = withDemoPersona(defaultUser);
      state.loggingOut = false;

      // Same priority as `login`: a saved profile locale on the JWT must win
      // over navigator/localStorage detection after a hard refresh. Without
      // this, clearing `i18nextLng` (or a cold tab with only the token) drops
      // back to en-US even when Mongo/JWT still carry uiLanguage (#4538 gap).
      // Prefer `decodedToken` (not `defaultUser`) — after applyToken mutations
      // TS can narrow `defaultUser` in a way that makes `.uiLanguage` `never`.
      const profileLanguage = decodedToken?.uiLanguage;
      if (profileLanguage) {
        import("../../i18n/config").then(({ applyProfileLanguage }) =>
          applyProfileLanguage(profileLanguage),
        );
      }
    },
    /**
     * Apply or clear a demo persona overlay. Persists to localStorage
     * (so the choice survives page reloads — `derive` re-applies on
     * boot) and immediately mutates the in-memory `state.value` so the
     * sidebar and other consumers re-render in the same tick. Pass
     * `null` to remove the overlay and restore the JWT's real name +
     * avatar.
     */
    setDemoPersona(state, action: PayloadAction<DemoPersona | null>) {
      setStoredDemoPersona(action.payload);
      if (!state.value) return;
      if (action.payload === null) {
        // Re-decode from localStorage to recover the canonical name +
        // avatar that the JWT carried. Cheaper than refetching /me.
        const token = safeStorageGet(localStorage, "token");
        if (token) {
          try {
            const decoded = jwtDecode<ShowcaseToken>(token);
            state.value = {
              ...state.value,
              name: decoded.name,
              avatar: decoded.avatar,
            };
          } catch {
            // Token parse failure here means the auth slice will
            // re-derive on the next page load anyway; nothing to do.
          }
        }
        return;
      }
      state.value = {
        ...state.value,
        name: action.payload.name,
        avatar: action.payload.avatar ?? state.value.avatar,
      };
    },
  },
});

export const { derive, login, logout, setDemoPersona } = authSlice.actions;
